import * as XLSX from 'xlsx-js-style';
import api from '../../../auth/apiClient';
import { isAxiosError } from 'axios';
import { trackDownload } from '../../../activity';

interface MutuBuahProps {
    selectedMonth: string;
    selectedEstate: number | null;
    setIsLoading: (loading: boolean) => void;
    months: Array<{ value: string; label: string }>;
    toast: any;
}

const MutuBuah = {
    export: async ({ selectedMonth, selectedEstate, setIsLoading, months, toast }: MutuBuahProps) => {

        try {
            const params: any = { month: selectedMonth };
            if (selectedEstate) {
                params.estate_id = selectedEstate;
            }
            const response = await api.get(
                '/api/inspection/report-mutu-buah',
                { params }
            );

            if (!response.data || response.data.length === 0) {
                toast({
                    title: 'Info',
                    description: 'Tidak ada data untuk dilaporkan pada periode terpilih.',
                    status: 'info',
                    duration: 3000,
                    isClosable: true,
                });
                setIsLoading(false);
                return;
            }

            const reportData = response.data;

            // Kolom yang akan diekspor
            const columnOrder = [
                'ESTATE',
                'DIVISI',
                'TANGGAL INSPEKSI',
                'NO POTONG',
                'NIP',
                'PEMANEN',
                'JABATAN',
                'KEMANDORAN',
                'SUPERVISOR',
                'JABATAN SUPERVISOR',
                'BLOK',
                'LUAS TANAM (HA)',
                'TPH',
                'MATANG (JJG)',
                'MENTAH (JJG)',
                'BUSUK & JANGKOS (JJG)',
                'TANGKAI PANJANG (JJG)',
                'BRONDOLAN (KG)',
                'TBS < 5 kg (JJG)',
                'BUAH BATU (JJG)',
                'DIMAKAN TIKUS (JJG)',
                'PARTHENOCARPY (JJG)',
                'KETERANGAN',
            ];

            // Mapping data ke format sheet
            const sheetData = reportData.map((item: any) => ({
                'ESTATE': item.estate,
                'DIVISI': item.divisi,
                'TANGGAL INSPEKSI': item.inspection_date,
                'NO POTONG': item.no_potong,
                'NIP': item.nip,
                'PEMANEN': item.pemanen,
                'JABATAN': item.job_name,
                'KEMANDORAN': item.foreman_group,
                'SUPERVISOR': item.supervisor,
                'JABATAN SUPERVISOR': item.spv_job_name,
                'BLOK': item.block,
                'LUAS TANAM (HA)': item.planted_area ? parseFloat(item.planted_area).toFixed(2) : '0.00',
                'TPH': item.tph,
                'MATANG (JJG)': item.ripe_qty === 0 ? '-' : item.ripe_qty,
                'MENTAH (JJG)': item.unripe_qty === 0 ? '-' : item.unripe_qty,
                'BUSUK & JANGKOS (JJG)': item.rooten_qty === 0 ? '-' : item.rooten_qty,
                'TANGKAI PANJANG (JJG)': item.lstalk_qty === 0 ? '-' : item.lstalk_qty,
                'BRONDOLAN (KG)': item.loose_qty === 0 ? '-' : item.loose_qty,
                'TBS < 5 kg (JJG)': item.abnormal_01_qty === 0 ? '-' : item.abnormal_01_qty,
                'DIMAKAN TIKUS (JJG)': item.abnormal_02_qty === 0 ? '-' : item.abnormal_02_qty,
                'BUAH BATU (JJG)': item.abnormal_03_qty === 0 ? '-' : item.abnormal_03_qty,
                'PARTHENOCARPY (JJG)': item.abnormal_04_qty === 0 ? '-' : item.abnormal_04_qty,
                'KETERANGAN': item.description ?? '',
            }));

            // Kelompokkan berdasarkan estate
            const groupedByEstate = sheetData.reduce((acc: any, item: any) => {
                if (!acc[item.ESTATE]) acc[item.ESTATE] = [];
                acc[item.ESTATE].push(item);
                return acc;
            }, {});

            const wb = XLSX.utils.book_new();

            for (const estateName in groupedByEstate) {
                const ws = XLSX.utils.json_to_sheet(groupedByEstate[estateName], { header: columnOrder });

                // Styling header
                for (let C = 0; C < columnOrder.length; ++C) {
                    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
                    if (!ws[cellAddress]) ws[cellAddress] = { v: columnOrder[C] };
                    ws[cellAddress].s = {
                        font: { bold: true, color: { rgb: "FFFFFF" } },
                        fill: { type: "pattern", pattern: "solid", fgColor: { rgb: "4A5A33" } },
                        alignment: { horizontal: "center", vertical: "center" },
                        border: {
                            top: { style: "thin", color: { rgb: "000000" } },
                            bottom: { style: "thin", color: { rgb: "000000" } },
                            left: { style: "thin", color: { rgb: "000000" } },
                            right: { style: "thin", color: { rgb: "000000" } }
                        }
                    };
                }

                // Styling untuk data rows dan format LUAS TANAM (HA)
                const rowCount = groupedByEstate[estateName].length;
                for (let R = 1; R <= rowCount; R++) {
                    for (let C = 0; C < columnOrder.length; C++) {
                        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
                        if (ws[cellAddress]) {
                            // Default border untuk semua cell
                            ws[cellAddress].s = {
                                alignment: { horizontal: "left", vertical: "center" }
                            };

                            // Format khusus untuk kolom LUAS TANAM (HA)
                            if (columnOrder[C] === 'LUAS TANAM (HA)') {
                                const value = ws[cellAddress].v;
                                if (value && !isNaN(parseFloat(value))) {
                                    ws[cellAddress].t = 'n'; // Set as number type
                                    ws[cellAddress].v = parseFloat(value); // Convert to number
                                    ws[cellAddress].z = '#,##0.00'; // Format with 2 decimals
                                    ws[cellAddress].s.alignment = { horizontal: "right", vertical: "center" };
                                }
                            }

                            // Format untuk kolom angka lainnya
                            if (['MATANG (JJG)', 'MENTAH (JJG)', 'BUSUK & JANGKOS (JJG)', 'TANGKAI PANJANG (JJG)', 'BRONDOLAN (KG)', 'TBS < 5 kg (JJG)', 'DIMAKAN TIKUS (JJG)', 'BUAH BATU (JJG)', 'PARTHENOCARPY (JJG)'].includes(columnOrder[C])) {
                                const value = ws[cellAddress].v;
                                // Jika angka, format number dan rata kanan
                                if (value && !isNaN(parseFloat(value))) {
                                    ws[cellAddress].t = 'n';
                                    ws[cellAddress].v = parseFloat(value);
                                    ws[cellAddress].z = '#,##0';
                                    ws[cellAddress].s.alignment = { horizontal: "right", vertical: "center" };
                                } else if (value === '-') {
                                    // Jika strip, tetap rata kanan
                                    ws[cellAddress].s.alignment = { horizontal: "right", vertical: "center" };
                                    ws[cellAddress].s.font = { italic: true, color: { rgb: "808080" } };
                                }
                            }
                        }
                    }
                }

                // Column widths
                ws['!cols'] = [
                    { wch: 20 }, // ESTATE
                    { wch: 18 }, // DIVISI
                    { wch: 15 }, // TANGGAL INSPEKSI
                    { wch: 15 }, // NO POTONG
                    { wch: 15 }, // NIP
                    { wch: 20 }, // PEMANEN
                    { wch: 15 }, // JABATAN
                    { wch: 30 }, // FOREMAN KEMANDORAN
                    { wch: 20 }, // SUPERVISOR
                    { wch: 18 }, // JABATAN SUPERVISOR
                    { wch: 15 }, // BLOK
                    { wch: 15 }, // LUAS TANAM (HA)
                    { wch: 15 }, // TPH
                    { wch: 15 },  // MATANG
                    { wch: 15 },  // MENTAH
                    { wch: 20 },  // BUSUK
                    { wch: 20 },  // TONGKOS
                    { wch: 15 }, // LOOSE FRUIT
                    { wch: 20 }, // ABNORMAL 1
                    { wch: 20 }, // ABNORMAL 2
                    { wch: 20 }, // ABNORMAL 3
                    { wch: 20 }, // ABNORMAL 4
                    { wch: 86 }, // KETERANGAN
                ];

                // Nama sheet aman
                const safeSheetName = estateName.replace(/[\\/*?[\]:]/g, "").substring(0, 30);
                XLSX.utils.book_append_sheet(wb, ws, safeSheetName);
            }

            const selectedMonthLabel = months.find(m => m.value === selectedMonth)?.label || selectedMonth;
            XLSX.writeFile(wb, `Laporan Mutu Buah ${selectedMonthLabel}.xlsx`);
            trackDownload('Laporan Mutu Buah', { month: selectedMonthLabel });
            toast({
                title: 'Success',
                description: 'Laporan mutu buah berhasil diekspor',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            let description = 'Gagal mengekspor laporan mutu buah';

            if (isAxiosError(error) && error.response) {
                description = `Gagal mengekspor laporan: ${error.response.data?.message || error.message}`;
            } else if (error instanceof Error) {
                description = `Gagal mengekspor laporan: ${error.message}`;
            }

            toast({
                title: 'Error',
                description,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            throw error;
        }
    }
};

export default MutuBuah;
